import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import Pagination from 'components/pagination/pagination';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import PillTabs from 'components/tabs/PillTabs';
import {
  addDays,
  clockLabel,
  dayLabel,
  fromIsoDay,
  isoDay,
} from 'helper/timeLabels';
import useHomeText from 'hooks/useHomeText';
import useMentions from 'hooks/useMentions';
import useScrolledShadow from 'hooks/useScrolledShadow';
import useTaskPanels from 'hooks/useTaskPanels';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LIST_GROUP_HEADER_CLASS } from 'utils/surfaceClasses';
import MentionItem, { MentionSkeleton } from './MentionItem';

export const MENTIONS_PANEL = 'mentions';

const PAGE_SIZE = 20;

// The shadow of SubNavbar, cast while the list runs beneath the header.
const HEADER_SHADOW_CLASS = 'shadow-[0_4px_6px_-4px_rgba(17,24,39,0.12)]';

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// The mentions under the head of their day, in the order they come.
const byDay = (mentions) =>
  mentions.reduce((groups, mention) => {
    const day = isoDay(new Date(mention.createdAt));
    const last = groups[groups.length - 1];
    if (last?.day === day) last.mentions.push(mention);
    else groups.push({ day, mentions: [mention] });
    return groups;
  }, []);

/**
 * All the comments that name the user, opened from « Voir tout » on the home
 * page: all of them or the unread ones only, by day, the latest first, 20 a
 * page. One opens its task on its comments, in the place of this panel.
 */
const MentionsPanel = () => {
  const { text } = useHomeText();
  const { toggleSlideOver } = useSlideOver();
  const { openTask } = useTaskPanels();
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const { mentions, total, totalPages, counts, failed, me, reload } =
    useMentions({ page, pageSize: PAGE_SIZE, filter });
  const { ref: scrolledRef, isScrolled } = useScrolledShadow();
  const listRef = useRef(null);

  const attachList = useCallback(
    (node) => {
      listRef.current = node;
      scrolledRef(node);
    },
    [scrolledRef],
  );

  // Unread mentions melt away as their tasks are opened: the page asked for
  // may no longer exist.
  useEffect(() => {
    if (totalPages >= 1 && page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [page, filter]);

  const changeFilter = (next) => {
    setFilter(next);
    setPage(1);
  };

  const now = new Date();
  const dayHead = (day) => {
    if (day === isoDay(now)) return text('tasks.today');
    if (day === isoDay(addDays(now, -1))) return text('tasks.yesterday');
    return capitalize(dayLabel(fromIsoDay(day)));
  };
  const rangeStart = (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <>
      <div
        data-testid="mentions-panel-header"
        className={`relative z-20 flex-shrink-0 bg-white transition-shadow duration-150 ${isScrolled ? HEADER_SHADOW_CLASS : ''}`}
      >
        <SlideOverHeader
          hide={() => toggleSlideOver()}
          closeLabel={text('mentions.close')}
        >
          {text('mentions.title')}
        </SlideOverHeader>
        <div className="px-4 pb-3 md:px-6">
          <PillTabs
            activeTab={filter}
            onChange={changeFilter}
            tabs={[
              {
                id: 'all',
                label: text('mentions.filterAll'),
                count: counts?.all,
              },
              {
                id: 'unread',
                label: text('mentions.filterUnread'),
                count: counts?.unread,
              },
            ]}
          />
        </div>
      </div>
      {/* Edge to edge, for the heads of the days to stick across it. */}
      <div
        ref={attachList}
        data-testid="mentions-panel-list"
        className="thin-scrollbar relative flex min-h-0 flex-1 flex-col overflow-y-auto border-t border-gray-100"
      >
        {failed && (
          <CompactEmptyState
            compact
            title={text('mentions.loadError')}
            action={{ label: text('mentions.retry'), onClick: reload }}
          />
        )}
        {!failed && !mentions && (
          <div className="px-2 py-3 md:px-4">
            <MentionSkeleton rows={4} />
          </div>
        )}
        {!failed && mentions?.length === 0 && (
          <CompactEmptyState
            compact
            glyph={filter === 'unread' ? 'badgeCheck' : 'inbox'}
            title={text(
              filter === 'unread'
                ? 'mentions.emptyUnreadTitle'
                : 'mentions.emptyTitle',
            )}
            description={text(
              filter === 'unread'
                ? 'mentions.emptyUnreadText'
                : 'mentions.emptyText',
            )}
          />
        )}
        {!failed &&
          byDay(mentions ?? []).map((group) => (
            <section key={group.day}>
              <p
                className={`sticky top-0 z-10 border-b border-gray-100 md:px-6 ${LIST_GROUP_HEADER_CLASS}`}
              >
                {dayHead(group.day)}
              </p>
              <ul className="flex flex-col px-2 py-1 md:px-4">
                {group.mentions.map((mention) => (
                  <li key={mention.id}>
                    <MentionItem
                      mention={mention}
                      me={me}
                      when={clockLabel(new Date(mention.createdAt))}
                      onOpen={() =>
                        openTask(mention.task.id, { tab: 'comments' })
                      }
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
      </div>
      {total > 0 && (
        <div className="flex-shrink-0 border-t border-gray-100 px-4 py-3 md:px-6">
          <Pagination
            currentPage={page}
            countPerPage={PAGE_SIZE}
            countTotal={total}
            totalPages={totalPages}
            setCurrentPage={setPage}
            customText={text('mentions.pageRange', {
              start: rangeStart,
              end: rangeEnd,
              total,
            })}
          />
        </div>
      )}
    </>
  );
};

export default MentionsPanel;
